import { fetcherWithToken, sendRequest } from '@/shared/api/fetcher';
import { setGtmUserId } from '@/shared/libs/analytics/gtm';
import useSWRMutation from 'swr/mutation';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { useConfirmDataPrompt } from '@/entities/data-confirmation/model/use-confirm-data-prompt';
import { useLoyaltyList } from '@/flat-pages/status/model/hooks';
import { useBonuses } from '@/entities/bonus/model/use-bonuses';
import { useProfile } from '@/entities/profile/model/use-profile';
import { useCurrency } from '@/shared/ui/selects/select-currency/model/hooks';
import { getIsTwaSession } from '@/shared/hooks/useTwaDetect';
import type { UserState } from '@/shared/providers/user-provider/user-context';

type RequestPayload = Record<string, unknown>;

interface AuthResponse {
  token: string;
  expired: number;
  error?: unknown;
}

interface LoginSuccessOptions {
  shouldSetCookie?: boolean;
  skipRedirect?: boolean;
}

interface PasswordOperationOptions {
  removeParam?: boolean;
}

function useAuthorization() {
  const { removeQueryParam } = useConfirmDataPrompt();
  const { mutateLoyaltyList } = useLoyaltyList();
  const { mutateBonuses } = useBonuses();
  const { mutate: mutateCurrency } = useCurrency();

  const router = useRouter();
  const { setUser } = useUser();
  const { trigger: twaMark } = useProfile();

  const handleLoginSuccess = async (
    token: string,
    expired: number,
    options: LoginSuccessOptions = {}
  ) => {
    const { shouldSetCookie = true, skipRedirect = false } = options;

    if (shouldSetCookie) {
      const days = expired / (24 * 60 * 60);
      Cookies.set('sessionId', token, { expires: days });
    }

    const [wishlist, profileData] = await Promise.all([
      triggers.getWishList(token),
      triggers.getProfile(token),
      mutateLoyaltyList(),
      mutateBonuses(),
      mutateCurrency(),
    ]);

    if (getIsTwaSession()) {
      const formData = new FormData();
      formData.append('appInstalled', '1');
      twaMark(formData);
    }

    setUser({
      ...profileData,
      wishlist,
      isAuth: true,
      authStatus: 'authenticated',
      isUserLoading: false,
    });
    setGtmUserId(profileData?.id);

    if (!skipRedirect) {
      router.push('/');
    }
  };

  const createTrigger = (endpoint: string) =>
    useSWRMutation<AuthResponse, unknown, string, RequestPayload>(
      endpoint,
      sendRequest
    ).trigger;

  const createTokenTrigger = <Data>(endpoint: string) =>
    useSWRMutation<Data, unknown, string, string>(endpoint, (url: string) =>
      fetcherWithToken(url)
    ).trigger;

  const triggers = {
    loginWithEmail: createTrigger('/login/email'),
    loginWithToken: createTrigger('/login/token'),
    loginWithPhone: createTrigger('/login/phone'),
    loginWithSms: createTrigger('/login/phone'),
    registerWithPhone: createTrigger('/register/phone'),
    confirmRegisterWithEmail: createTrigger('/register/email/confirmation'),
    recoveryPasswordWithEmail: createTrigger('/recover/email'),
    recoveryPasswordWithPhone: createTrigger('/recover/phone'),
    createPasswordWithEmail: createTrigger('/recover'),
    getProfile: createTokenTrigger<Partial<UserState>>(
      '/profile/getProfileData'
    ),
    getWishList: createTokenTrigger<number[]>('/favorites'),
    register: createTrigger('/register'),
  };

  type RequestTriggerKey = Exclude<
    keyof typeof triggers,
    'getProfile' | 'getWishList'
  >;
  type RequestTrigger = (typeof triggers)[RequestTriggerKey];

  const authenticate = async (
    values: RequestPayload,
    stateKey: RequestTriggerKey
  ) => {
    try {
      const response = await triggers[stateKey](values);

      if (response.error && stateKey !== 'loginWithToken') {
        return { success: false };
      }

      if (stateKey.startsWith('login')) {
        await handleLoginSuccess(response.token, response.expired);
      } else {
        router.push('/');
      }

      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const resendRequest = async (
    trigger: RequestTrigger,
    data: RequestPayload
  ) => {
    try {
      const response = await trigger(data);
      return response.error ? { success: false } : { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          typeof error === 'object' && error !== null && 'message' in error
            ? error.message
            : undefined,
      };
    }
  };

  const emailCodeAuthenticate = async (
    code: string,
    fromProfile: boolean | string = false
  ) => {
    if (!fromProfile) {
      setUser((prev) => ({
        ...prev,
        authStatus: 'checking',
        isUserLoading: true,
      }));
    }

    try {
      const response = await triggers.confirmRegisterWithEmail({ code });
      if (response.error) return { success: false };

      await handleLoginSuccess(response.token, response.expired, {
        shouldSetCookie: !fromProfile,
        skipRedirect: true,
      });

      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const passwordOperation = async (
    trigger: RequestTrigger,
    values: RequestPayload,
    options: PasswordOperationOptions = {}
  ) => {
    try {
      const response = await trigger(values);
      await handleLoginSuccess(response.token, response.expired);
      if (options.removeParam) removeQueryParam('recoveryCode');
      return { success: true };
    } catch (error) {
      return { success: false, error: error };
    }
  };

  const createPasswordWithEmail = (values: RequestPayload) =>
    passwordOperation(triggers.createPasswordWithEmail, values, {
      removeParam: true,
    });

  const register = (values: RequestPayload) =>
    passwordOperation(triggers.register, values);

  const recoveryPassword = (type: 'Email' | 'Phone', values: RequestPayload) =>
    resendRequest(triggers[`recoveryPasswordWith${type}`], values);

  const { trigger: smsAuthenticate } = useSWRMutation(
    '/register/phone/confirmation',
    (url: string, { arg }: { arg: RequestPayload }) =>
      fetcherWithToken(url, {
        method: 'POST',
        body: JSON.stringify(arg),
      })
  );

  return {
    authenticate,
    smsAuthenticate,
    resendSms: (form: RequestPayload) =>
      resendRequest(triggers.registerWithPhone, form),
    resendRecoveryEmail: (form: RequestPayload) =>
      recoveryPassword('Email', { email: form.email }),
    emailCodeAuthenticate,
    recoveryPasswordWithEmail: (values: RequestPayload) =>
      recoveryPassword('Email', values),
    recoveryPasswordWithPhone: (values: RequestPayload) =>
      recoveryPassword('Phone', values),
    createPasswordWithEmail,
    register,
    handleLoginSuccess,
  };
}

export { useAuthorization };
